import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCreateProperty } from '../hooks/useCreateProperty';
import { useImageUpload } from '../hooks/useImageUpload';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { ArrowLeft, Plus, X, Upload, ArrowUp, ArrowDown, GripVertical } from 'lucide-react';

const AddProperty = () => {
  const navigate = useNavigate();
  const { mutate: createProperty, isPending } = useCreateProperty();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    address: '',
    type: '',
    pricePerNight: '',
    size: '',
    bedrooms: '',
    bathrooms: '',
    images: [],
    features: [],
    notes: '',
  });

  const { dragActive, handleImageInput, handleDrag, handleDrop, removeImage, moveImage, draggedIndex, handleImageDragStart, handleImageDragOver, handleImageDrop } = useImageUpload((updateFn) => {
    setFormData((prev) => ({
      ...prev,
      images: updateFn(prev.images),
    }));
  });

  const commonFeatures = [
    'Furnished',
    'WiFi',
    'Parking',
    'Garden',
    'Balcony',
    'Air Conditioning',
    'Heating',
    'Dishwasher',
  ];


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value === '' ? '' : isNaN(value) ? value : Number(value),
    }));
  };

  const handleArrayInput = (e, field) => {
    const value = e.target.value.trim();
    if (e.key === 'Enter' && value) {
      e.preventDefault();
      setFormData((prev) => ({
        ...prev,
        [field]: [...prev[field], value],
      }));
      e.target.value = '';
    }
  };

  const removeArrayItem = (field, index) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index),
    }));
  };

  const toggleFeature = (feature) => {
    setFormData((prev) => ({
      ...prev,
      features: prev.features.includes(feature)
        ? prev.features.filter((f) => f !== feature)
        : [...prev.features, feature],
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.title || !formData.address || !formData.type || !formData.pricePerNight || !formData.size || !formData.bedrooms || !formData.bathrooms) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (formData.features.length === 0) {
      toast.error('Please select at least 1 feature');
      return;
    }

    if (formData.images.length === 0) {
      toast.error('Please add at least 1 image');
      return;
    }

    const loadingToastId = toast.loading('Creating property...');
    createProperty(formData, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['properties'] });
        toast.dismiss(loadingToastId);
        toast.success('Property added successfully!');
        navigate('/properties');
      },
      onError: (error) => {
        toast.dismiss(loadingToastId);
        toast.error(error.message || 'Failed to add property');
      },
    });
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex items-start space-x-3 sm:space-x-4">
        <button
          onClick={() => navigate('/properties')}
          className="p-2 hover:bg-gray-100 rounded-lg transition duration-200 flex-shrink-0"
        >
          <ArrowLeft size={20} className="text-prose-brand" />
        </button>
        <div className="min-w-0">
          <h1 className="text-2xl sm:text-3xl font-bold text-prose-brand break-words">Add New Property</h1>
          <p className="text-xs sm:text-sm text-prose-black/60 mt-1">Create a new property listing</p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="bg-white rounded-lg border border-gray-300 p-4 sm:p-6">
          <h2 className="text-lg sm:text-xl font-bold text-prose-brand mb-4">Basic Information</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-prose-black mb-1">
                Property Title *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="e.g., Luxury Apartment in City Centre"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-prose-brand"
              />
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-prose-black mb-1">
                Address *
              </label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                placeholder="Full address"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-prose-brand text-sm"
              />
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-prose-black mb-1">
                Property Type *
              </label>
              <input
                type="text"
                name="type"
                value={formData.type}
                onChange={handleInputChange}
                placeholder="e.g., Professional HMO, Luxury Apartment"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-prose-brand text-sm"
              />
            </div>

          </div>
        </div>

        {/* Notes */}
        <div className="bg-white rounded-lg border border-gray-300 p-4 sm:p-6">
          <h2 className="text-lg sm:text-xl font-bold text-prose-brand mb-4">Description *</h2>

          <textarea
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            placeholder="Add detailed description, house rules, etc."
            rows="6"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-prose-brand resize-none text-sm"
          />
        </div>

        {/* Pricing & Size */}
        <div className="bg-white rounded-lg border border-gray-300 p-4 sm:p-6">
          <h2 className="text-lg sm:text-xl font-bold text-prose-brand mb-4">Pricing & Specifications</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-prose-black mb-1">
                Price Per Night (£) *
              </label>
              <input
                type="number"
                name="pricePerNight"
                value={formData.pricePerNight}
                onChange={handleInputChange}
                min="0"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-prose-brand"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-prose-black mb-1">
                Size (sq ft) *
              </label>
              <input
                type="number"
                name="size"
                value={formData.size}
                onChange={handleInputChange}
                min="0"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-prose-brand"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-prose-black mb-1">
                Bedrooms *
              </label>
              <input
                type="number"
                name="bedrooms"
                value={formData.bedrooms}
                onChange={handleInputChange}
                min="1"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-prose-brand"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-prose-black mb-1">
                Bathrooms *
              </label>
              <input
                type="number"
                name="bathrooms"
                value={formData.bathrooms}
                onChange={handleInputChange}
                min="1"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-prose-brand"
              />
            </div>
          </div>
        </div>

        {/* Images */}
        <div className="bg-white rounded-lg border border-gray-300 p-6">
          <h2 className="text-xl font-bold text-prose-brand mb-4">Images *</h2>
          <p className="text-xs sm:text-sm text-prose-black/60 mb-4">At least 1 image required. Only JPG and PNG images are allowed, max 5MB each</p>

          {/* Drag and Drop Area */}
          <div
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-lg p-8 text-center transition ${dragActive ? 'border-prose-brand bg-prose-brand/5' : 'border-gray-300'}`}
          >
            <Upload size={40} className="mx-auto text-prose-brand/60 mb-3" />
            <p className="text-prose-black font-medium mb-2">Drag and drop images here</p>
            <p className="text-prose-black/60 text-sm mb-4">or</p>
            <label className="inline-flex items-center space-x-2 px-6 py-2 bg-prose-brand text-white rounded-lg hover:bg-prose-brand/90 cursor-pointer">
              <Upload size={18} />
              <span>Select Images</span>
              <input
                type="file"
                accept="image/jpeg,image/png"
                multiple
                onChange={handleImageInput}
                className="hidden"
              />
            </label>
          </div>

          {/* Uploaded Images Grid */}
          {formData.images.length > 0 && (
            <div className="mt-6">
              <p className="text-xs sm:text-sm font-medium text-prose-black mb-3">Uploaded Images ({formData.images.length})</p>
              <p className="text-xs text-prose-black/50 mb-3">Drag images to reorder or use arrow buttons. The first image will be the primary thumbnail.</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4">
                {formData.images.map((image, index) => (
                  <div
                    key={index}
                    draggable
                    onDragStart={() => handleImageDragStart(index)}
                    onDragOver={handleImageDragOver}
                    onDrop={() => handleImageDrop(index)}
                    className={`relative group cursor-move transition ${
                      draggedIndex === index ? 'opacity-50' : ''
                    }`}
                  >
                    {/* Image Container */}
                    <div className="w-full aspect-square rounded-lg border-2 border-gray-300 overflow-hidden hover:border-prose-brand transition">
                      <img
                        src={image instanceof File ? URL.createObjectURL(image) : image}
                        alt={`Upload ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>

                    {/* Image Index Badge */}
                    <div className="absolute top-1 left-1 sm:top-2 sm:left-2 px-2 py-1 bg-prose-brand text-white text-xs font-semibold rounded">
                      {index + 1}
                    </div>

                    {/* Drag Handle */}
                    <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 group-hover:opacity-100 transition rounded-lg">
                      <GripVertical size={24} className="text-white" />
                    </div>

                    {/* Action Buttons */}
                    <div className="absolute bottom-1 right-1 sm:bottom-2 sm:right-2 flex gap-1 sm:gap-2 opacity-0 group-hover:opacity-100 transition">
                      {/* Move Up Button */}
                      <button
                        type="button"
                        onClick={() => index > 0 && moveImage(index, index - 1)}
                        disabled={index === 0}
                        className="p-1 sm:p-1.5 bg-blue-500 text-white rounded-full hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition"
                        title="Move image up in order"
                      >
                        <ArrowUp size={14} className="sm:w-4 sm:h-4" />
                      </button>

                      {/* Move Down Button */}
                      <button
                        type="button"
                        onClick={() => index < formData.images.length - 1 && moveImage(index, index + 1)}
                        disabled={index === formData.images.length - 1}
                        className="p-1 sm:p-1.5 bg-blue-500 text-white rounded-full hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition"
                        title="Move image down in order"
                      >
                        <ArrowDown size={14} className="sm:w-4 sm:h-4" />
                      </button>

                      {/* Remove Button */}
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="p-1 sm:p-1.5 bg-red-600 text-white rounded-full hover:bg-red-700 transition"
                        title="Remove image"
                      >
                        <X size={14} className="sm:w-4 sm:h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Features */}
        <div className="bg-white rounded-lg border border-gray-300 p-4 sm:p-6">
          <h2 className="text-lg sm:text-xl font-bold text-prose-brand mb-4">Features *</h2>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3 mb-4">
            {commonFeatures.map((feature) => (
              <label key={feature} className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.features.includes(feature)}
                  onChange={() => toggleFeature(feature)}
                  className="w-4 h-4 rounded border-gray-300 text-prose-brand"
                />
                <span className="text-xs sm:text-sm text-prose-black">{feature}</span>
              </label>
            ))}
          </div>

          <div>
            <label className="block text-xs sm:text-sm font-medium text-prose-black mb-2">
              Custom Features (Press Enter to add)
            </label>
            <input
              type="text"
              onKeyPress={(e) => handleArrayInput(e, 'features')}
              placeholder="Add custom feature..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-prose-brand text-sm"
            />
            <div className="flex flex-wrap gap-2 mt-3">
              {formData.features.map((feature, index) => (
                <span
                  key={index}
                  className="px-2 sm:px-3 py-1 rounded-lg text-xs sm:text-sm bg-prose-gold/20 text-prose-brand font-medium flex items-center space-x-2"
                >
                  <span>{feature}</span>
                  <button
                    type="button"
                    onClick={() => removeArrayItem('features', index)}
                    className="hover:text-red-600"
                  >
                    <X size={12} className="sm:w-3.5 sm:h-3.5" />
                  </button>
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Additional Notes */}
        <div className="bg-white rounded-lg border border-gray-300 p-4 sm:p-6">
          <h2 className="text-lg sm:text-xl font-bold text-prose-brand mb-4">Additional Notes</h2>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleInputChange}
            placeholder="Add any additional notes about the property..."
            rows="5"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-prose-brand text-sm resize-none"
          />
        </div>

        {/* Form Actions */}
        <div className="flex gap-2 sm:gap-3">
          <button
            type="submit"
            disabled={isPending}
            className="flex items-center justify-center space-x-2 px-4 sm:px-6 py-2 bg-prose-brand text-white rounded-lg hover:bg-prose-brand/90 transition duration-200 font-semibold text-xs sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus size={18} />
            <span>{isPending ? 'Adding...' : 'Add Property'}</span>
          </button>
          <button
            type="button"
            disabled={isPending}
            onClick={() => navigate('/properties')}
            className="px-4 sm:px-6 py-2 border border-gray-300 text-prose-brand rounded-lg hover:bg-gray-100 transition duration-200 font-semibold text-xs sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddProperty;
